import { useMemo, useState } from "react";
import { ArrowLeft, Check, Home, MapPin, Plus, Search, X } from "lucide-react";
import { generateId, useApp, type StorageBin } from "@/context/AppContext";
import { ModalSheet } from "@/components/Modal";

export const BIN_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#eab308",
  "#ef4444",
  "#a855f7",
  "#f97316",
  "#ec4899",
  "#14b8a6",
  "#6b7280",
  "#6366f1",
];

interface Props {
  open: boolean;
  value: string;
  onSelect: (binName: string) => void;
  onClose: () => void;
}

export function BinPickerModal({ open, value, onSelect, onClose }: Props) {
  const { storageBins, addBin, locations } = useApp();

  // TNK-103 Phase G.4 polish. When more than one non-archived Location
  // exists, the bin list is grouped under Location headers; otherwise it
  // stays a flat scroll. The default `newLocationId` for the create form
  // prefers the seeded "Home" location and falls back to the first active.
  const activeLocations = useMemo(
    () => locations.filter((l) => !l.archivedAt),
    [locations],
  );
  const showLocationUi = activeLocations.length > 1;
  const defaultLocationId = useMemo(
    () =>
      activeLocations.find((l) => l.name === "Home")?.id ??
      activeLocations[0]?.id ??
      "",
    [activeLocations],
  );

  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(BIN_COLORS[0]);
  const [newLocationId, setNewLocationId] = useState<string>("");
  const [nameError, setNameError] = useState("");

  const filtered = storageBins.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase()),
  );

  // Group filtered bins under their Location for the multi-location view.
  // Bins whose locationId doesn't match an active Location (orphans from
  // schema drift) fall back to the default location's bucket.
  const groupedFiltered = useMemo(() => {
    const map = new Map<string, StorageBin[]>();
    for (const bin of filtered) {
      const locId = bin.locationId ?? defaultLocationId ?? "";
      const arr = map.get(locId) ?? [];
      arr.push(bin);
      map.set(locId, arr);
    }
    return map;
  }, [filtered, defaultLocationId]);

  const reset = () => {
    setSearch("");
    setShowCreate(false);
    setNewName("");
    setNewLocationId("");
    setNameError("");
  };

  const handleSelect = (name: string) => {
    onSelect(name);
    reset();
    onClose();
  };

  const handleClear = () => handleSelect("");

  const handleCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setNameError("Bin name is required");
      return;
    }
    const isDupe = storageBins.some(
      (b) => b.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDupe) {
      setNameError("A bin with this name already exists");
      return;
    }
    const bin: StorageBin = {
      id: generateId("BIN"),
      name: trimmed,
      color: newColor,
      locationId: newLocationId || defaultLocationId || undefined,
      createdAt: new Date().toISOString(),
    };
    addBin(bin);
    handleSelect(bin.name);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ModalSheet
      open={open}
      onClose={handleClose}
      ariaLabel="Select storage bin"
    >
      <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
        <h2 className="text-base font-semibold text-foreground">
          Select Storage Bin
        </h2>
        <button
          type="button"
          onClick={handleClose}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {!showCreate ? (
        <>
          <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
            <Search className="h-[15px] w-[15px] text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bins…"
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              autoComplete="off"
            />
            {search.length > 0 && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="mt-1 max-h-[55vh] overflow-y-auto px-4">
            {value !== "" && (
              <button
                type="button"
                onClick={handleClear}
                className="flex w-full items-center gap-3 border-b border-border px-1 py-3 text-left hover:bg-muted/40"
              >
                <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-border">
                  <X className="h-3 w-3 text-muted-foreground" />
                </span>
                <span className="text-sm font-medium text-muted-foreground">
                  Unassigned
                </span>
              </button>
            )}

            {showLocationUi
              ? activeLocations.map((loc) => {
                  const binsHere = groupedFiltered.get(loc.id) ?? [];
                  if (binsHere.length === 0) return null;
                  return (
                    <div key={loc.id} className="pb-2">
                      <div className="flex items-center gap-1.5 px-1 pb-1 pt-3">
                        {loc.name === "Home" ? (
                          <Home className="h-3 w-3 text-muted-foreground" />
                        ) : (
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                        )}
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {loc.name}
                        </span>
                      </div>
                      {binsHere.map((bin) => (
                        <BinRow
                          key={bin.id}
                          bin={bin}
                          isSelected={value === bin.name}
                          onSelect={handleSelect}
                        />
                      ))}
                    </div>
                  );
                })
              : filtered.map((bin) => (
                  <BinRow
                    key={bin.id}
                    bin={bin}
                    isSelected={value === bin.name}
                    onSelect={handleSelect}
                  />
                ))}

            {filtered.length === 0 && search.length > 0 && (
              <p className="py-6 text-center text-[13px] text-muted-foreground">
                No bins match &quot;{search}&quot;
              </p>
            )}

            {storageBins.length === 0 && search.length === 0 && (
              <p className="py-6 text-center text-[13px] text-muted-foreground">
                No bins yet — tap below to create one
              </p>
            )}

            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="flex w-full items-center gap-3 border-b border-border px-1 py-3 text-left hover:bg-muted/40"
            >
              <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-primary/40 bg-primary/10">
                <Plus className="h-3 w-3 text-primary" />
              </span>
              <span className="text-sm font-medium text-primary">New bin…</span>
            </button>
          </div>
        </>
      ) : (
        <div className="space-y-5 px-5 pb-6 pt-4">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setNameError("");
                setNewName("");
              }}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Back"
            >
              <ArrowLeft className="h-[18px] w-[18px]" />
            </button>
            <h3 className="text-[15px] font-semibold text-foreground">
              New Storage Bin
            </h3>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Bin Name *
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setNameError("");
              }}
              placeholder="e.g. Box A, Binder 1, Top Shelf"
              autoFocus
              className={`w-full rounded-lg border bg-card px-3.5 py-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary ${
                nameError ? "border-destructive" : "border-border"
              }`}
            />
            {nameError && (
              <p className="text-xs text-destructive">{nameError}</p>
            )}
          </div>

          {showLocationUi && (
            <div className="space-y-2">
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Location
              </label>
              <select
                value={newLocationId || defaultLocationId}
                onChange={(e) => setNewLocationId(e.target.value)}
                className="w-full rounded-lg border border-border bg-card px-3.5 py-3 text-[15px] text-foreground focus:border-primary focus:outline-none"
              >
                {activeLocations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Color
            </label>
            <div className="flex flex-wrap gap-2.5">
              {BIN_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={`flex h-8 w-8 items-center justify-center rounded-full transition-transform ${
                    newColor === c
                      ? "ring-2 ring-white ring-offset-2 ring-offset-card scale-105"
                      : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: c }}
                  aria-label={`Color ${c}`}
                >
                  {newColor === c && <Check className="h-3 w-3 text-white" />}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handleCreate}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3.5 text-[15px] font-bold text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Create Bin
          </button>
        </div>
      )}
    </ModalSheet>
  );
}

/** Single bin row inside the picker — extracted so the flat (single-location)
 *  list and the grouped (multi-location) list share the same chrome. */
function BinRow({
  bin,
  isSelected,
  onSelect,
}: {
  bin: StorageBin;
  isSelected: boolean;
  onSelect: (name: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(bin.name)}
      className="flex w-full items-center gap-3 border-b border-border px-1 py-3 text-left transition-colors hover:bg-muted/40"
      style={{
        borderColor: isSelected ? `${bin.color}88` : undefined,
        backgroundColor: isSelected ? `${bin.color}12` : undefined,
      }}
    >
      <span
        className="h-[18px] w-[18px] shrink-0 rounded-full"
        style={{ backgroundColor: bin.color }}
      />
      <span className="flex-1 text-sm font-medium text-foreground">
        {bin.name}
      </span>
      {isSelected && <Check className="h-4 w-4" style={{ color: bin.color }} />}
    </button>
  );
}
